import { Helmet } from "react-helmet-async";
import { ShieldCheck } from "lucide-react";
import { WebsiteHeader } from "@/components/layout/website-header";
import { WebsiteFooter } from "@/components/layout/website-footer";

const LAST_UPDATED = "October 14, 2026";

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <Helmet>
        <title>Privacy Policy | SaaSGama</title>
        <meta name="description" content="Read SaaSGama's Privacy Policy to learn how we collect, use, disclose, and safeguard your information across our SaaS management platform." />
        <meta name="keywords" content="SaaSGama Privacy Policy, Data Protection, Privacy, GDPR" />
        <link rel="canonical" href="https://saasgama.com/privacy-policy" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://saasgama.com/privacy-policy" />
        <meta property="og:site_name" content="SaaSGama" />
        <meta property="og:title" content="Privacy Policy | SaaSGama" />
        <meta property="og:description" content="Read SaaSGama's Privacy Policy to learn how we collect, use, disclose, and safeguard your information across our SaaS management platform." />
        <meta property="og:image" content="https://saasgama.com/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Privacy Policy | SaaSGama" />
        <meta name="twitter:description" content="Read SaaSGama's Privacy Policy to learn how we collect, use, disclose, and safeguard your information across our SaaS management platform." />
        <meta name="twitter:image" content="https://saasgama.com/og-image.png" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            { "@type": "Organization", "@id": "https://saasgama.com/#organization", "name": "SaaSGama", "url": "https://saasgama.com", "logo": "https://saasgama.com/logo.png" },
            { "@type": "BreadcrumbList", "@id": "https://saasgama.com/privacy-policy#breadcrumb", "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://saasgama.com" },
              { "@type": "ListItem", "position": 2, "name": "Privacy Policy", "item": "https://saasgama.com/privacy-policy" }
            ]},
            { "@type": "WebPage", "@id": "https://saasgama.com/privacy-policy#webpage", "url": "https://saasgama.com/privacy-policy", "name": "Privacy Policy | SaaSGama", "description": "How SaaSGama collects, uses, discloses, and safeguards your information.", "breadcrumb": { "@id": "https://saasgama.com/privacy-policy#breadcrumb" } }
          ]
        })}</script>
      </Helmet>

      <WebsiteHeader showBackButton />

      {/* Hero */}
      <section className="pt-32 pb-12 bg-gradient-to-br from-gray-900 via-blue-900 to-indigo-900 text-white">
        <div className="container mx-auto px-6 max-w-4xl text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <ShieldCheck className="w-4 h-4" />
            Your Privacy Matters
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Privacy Policy</h1>
          <p className="text-blue-100">Last updated: {LAST_UPDATED}</p>
        </div>
      </section>

      {/* Policy content */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 md:p-12 space-y-10 text-gray-700 leading-relaxed">

            <section>
              <h2 className="font-semibold text-xl text-gray-900 mb-3">1. Introduction</h2>
              <p>
                SaaSGama ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains
                how we collect, use, disclose, and safeguard your information when you use our SaaS management platform
                and related services. For details on how we use cookies specifically, see our{" "}
                <a href="/cookie-policy" className="text-blue-600 hover:underline">Cookie Policy</a>.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-xl text-gray-900 mb-3">2. Information We Collect</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="font-medium text-gray-900">Account Information</h3>
                  <p>Name, email address, company name, phone number, and billing information.</p>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">SaaS Management Data</h3>
                  <p>Information about your software subscriptions, licenses, usage analytics, renewal dates, and cost management data.</p>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Technical Information</h3>
                  <p>IP address, browser type, device information, cookies, and usage analytics.</p>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Communication Data</h3>
                  <p>Messages you send us and records of our interactions with you.</p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="font-semibold text-xl text-gray-900 mb-3">3. How We Use Your Information</h2>
              <ul className="list-disc list-inside space-y-2">
                <li>Provide and maintain our SaaS management platform</li>
                <li>Process transactions and manage your subscriptions</li>
                <li>Send you renewal notifications and cost optimization insights</li>
                <li>Provide customer support and respond to inquiries</li>
                <li>Improve our services and develop new features</li>
                <li>Comply with legal obligations and protect against fraud</li>
                <li>Send marketing communications (with your consent)</li>
              </ul>
            </section>

            <section>
              <h2 className="font-semibold text-xl text-gray-900 mb-3">4. Information Sharing and Disclosure</h2>
              <p>We do not sell your personal information. We may share your information in these limited circumstances:</p>
              <ul className="list-disc list-inside space-y-2 mt-2">
                <li><strong>Service Providers:</strong> Third-party vendors who help us operate our platform (payment processors, email services, analytics providers)</li>
                <li><strong>Business Transfers:</strong> In connection with mergers, acquisitions, or sale of assets</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights and safety</li>
                <li><strong>With Your Consent:</strong> When you explicitly authorize us to share specific information</li>
              </ul>
            </section>

            <section>
              <h2 className="font-semibold text-xl text-gray-900 mb-3">5. Data Security</h2>
              <p>
                We implement industry-standard security measures including encryption, secure data transmission,
                access controls, and regular security audits to protect your information. However, no method of
                electronic storage or transmission is 100% secure.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-xl text-gray-900 mb-3">6. Your Rights</h2>
              <p>Depending on your location, you may have the following rights:</p>
              <ul className="list-disc list-inside space-y-2 mt-2">
                <li><strong>Access:</strong> Request access to your personal information</li>
                <li><strong>Correction:</strong> Request correction of inaccurate information</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information</li>
                <li><strong>Portability:</strong> Request a copy of your data in a portable format</li>
                <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
                <li><strong>Object:</strong> Object to certain processing activities</li>
              </ul>
              <p className="mt-2">To exercise these rights, contact us at privacy@saasgama.com.</p>
            </section>

            <section>
              <h2 className="font-semibold text-xl text-gray-900 mb-3">7. Data Retention</h2>
              <p>
                We retain your personal information for as long as necessary to provide our services and comply with
                legal obligations. Account information is typically retained for 7 years after account closure for
                compliance purposes.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-xl text-gray-900 mb-3">8. International Transfers</h2>
              <p>
                Your information may be transferred to and processed in countries outside your residence. We ensure
                appropriate safeguards are in place for international transfers, including standard contractual clauses
                and adequacy decisions.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-xl text-gray-900 mb-3">9. Children's Privacy</h2>
              <p>
                Our services are not intended for individuals under 18 years of age. We do not knowingly collect
                personal information from children under 18.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-xl text-gray-900 mb-3">10. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy periodically. We will notify you of material changes via email
                or through our platform. Continued use of our services after changes constitutes acceptance of the
                updated policy.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-xl text-gray-900 mb-3">11. Contact Us</h2>
              <p>If you have questions about this Privacy Policy, please contact us:</p>
              <div className="mt-2 space-y-1">
                <p><strong>Email:</strong> privacy@saasgama.com</p>
                <p><strong>Address:</strong> 5 Shenton Wy, #10-01, UIC Building, Singapore 068808</p>
                <p><strong>Phone:</strong> +65 8894 3169</p>
              </div>
            </section>

          </div>
        </div>
      </section>

      <WebsiteFooter />
    </div>
  );
}
